import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { YaprioLogo } from "@/components/YaprioLogo";
import AuthModal from "@/components/AuthModal";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { Video, Users, MessageCircle, Shield, Globe, ArrowRight } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1763930827024-e7d41f14bde3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHw0fHxwaW5rJTIwM2QlMjBidWJibGUlMjBzcGhlcmUlMjBiYWNrZ3JvdW5kJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzg4Nzg4MTExfDA&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { icon: Video, title: "Random Video Chat", desc: "Meet people face-to-face" },
  { icon: Users, title: "Make Friends", desc: "Connect with new people" },
  { icon: MessageCircle, title: "Text Chat & Messaging", desc: "Chat privately or in real time" },
  { icon: Globe, title: "Explore", desc: "Find people around the world" },
  { icon: Shield, title: "Stay Safe", desc: "Your privacy comes first" },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [mode, setMode] = useState("signup");

  useEffect(() => {
    if (!loading && user) navigate("/home", { replace: true });
  }, [user, loading, navigate]);

  const openAuth = (m) => {
    setMode(m);
    setAuthOpen(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5 sm:px-8">
        <YaprioLogo />
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={() => openAuth("login")}
            data-testid="btn-login-trigger"
            className="rounded-full font-semibold text-slate-700"
          >
            {t("Log in")}
          </Button>
          <Button
            onClick={() => openAuth("signup")}
            data-testid="btn-signup-trigger"
            className="rounded-full bg-pink-500 font-semibold text-white shadow-lg shadow-pink-500/30 hover:bg-pink-600"
          >
            {t("Sign up")}
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 pb-20 sm:px-8">
        <section className="relative mt-6 overflow-hidden rounded-[2rem] border border-slate-800/70 bg-gradient-to-br from-[#111115] via-[#18181C] to-[#0f0f12] p-8 shadow-2xl sm:p-14">
          <img
            src={HERO_IMG}
            alt=""
            aria-hidden
            className="pointer-events-none absolute right-0 top-0 h-full w-1/2 object-cover opacity-70 [mask-image:linear-gradient(to_left,black,transparent)]"
          />
          <div className="pointer-events-none absolute -left-20 top-10 h-64 w-64 rounded-full bg-pink-500/20 blur-3xl" />
          <div className="relative max-w-xl">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-pink-400">
              {t("Welcome to Yaprio")}
            </p>
            <h1 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl">
              {t("Meet people.")}
              <br />
              {t("Make friends.")}
              <br />
              <span className="text-pink-400">{t("Talk face-to-face.")}</span>
            </h1>
            <p className="mt-5 max-w-md text-base leading-relaxed text-slate-300">
              {t("Random video chat, text chat, private messaging, and a real community — built for genuine connections instead of endless scrolling.")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                onClick={() => openAuth("signup")}
                data-testid="btn-get-started"
                className="h-12 gap-2 rounded-full bg-pink-500 px-7 text-base font-semibold text-white shadow-lg shadow-pink-500/30 hover:bg-pink-600"
              >
                {t("Get started")} <ArrowRight className="h-4 w-4" />
              </Button>
              <Button
                onClick={() => openAuth("login")}
                variant="outline"
                className="h-12 rounded-full border-slate-600 bg-transparent px-7 text-base font-semibold text-slate-200 hover:bg-slate-800 hover:text-white"
              >
                {t("I have an account")}
              </Button>
            </div>
            <p className="mt-8 font-script text-2xl text-pink-400">good vibes only ♥</p>
          </div>
        </section>

        <section className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="group rounded-2xl border border-pink-100/80 bg-pink-50/60 p-5 transition-all hover:-translate-y-0.5 hover:bg-pink-50 hover:shadow-md"
            >
              <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-white shadow-sm">
                <Icon className="h-5 w-5 text-pink-500" />
              </div>
              <h3 className="font-heading text-sm font-bold text-slate-900">{t(title)}</h3>
              <p className="mt-1 text-xs text-slate-500">{t(desc)}</p>
            </div>
          ))}
        </section>
      </main>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode={mode} />
    </div>
  );
}
